import { Autocomplete, Stack, TextField, Typography } from '@mui/material';
import { useQuery } from '@parkchirp/api-client';
import { useMemo } from 'react';
import { LIST_ORGANIZATIONS } from '../../queries';

export type OrganizationOption = {
	id: number;
	name: string;
};

type OrganizationsResponse = {
	listOrganizations: {
		id: string;
		organizationId: number;
		name: string | null;
	}[];
};

type Props = {
	value: number | null;
	onChange: (value: number | null) => void;
};

export const useOrganizationOptions = () => {
	const { data, loading } = useQuery<OrganizationsResponse>(LIST_ORGANIZATIONS, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
	});

	const options = useMemo<OrganizationOption[]>(() => {
		const organizations = data?.listOrganizations ?? [];

		return organizations
			.filter((organization) => Boolean(organization.name))
			.map((organization) => ({
				id: organization.organizationId,
				name: organization.name as string,
			}));
	}, [data]);

	return { options, loading };
};

export const OrganizationFilter = ({ value, onChange }: Props) => {
	const { options, loading } = useOrganizationOptions();

	const selected = options.find((option) => option.id === value) ?? null;

	return (
		<Stack spacing={2}>
			<Typography variant='subtitle1' fontWeight={600}>
				Organization
			</Typography>

			<Autocomplete
				options={options}
				value={selected}
				loading={loading}
				getOptionLabel={(option) => option.name}
				isOptionEqualToValue={(option, selectedOption) => option.id === selectedOption.id}
				onChange={(_, option) => {
					onChange(option?.id ?? null);
				}}
				renderInput={(params) => <TextField {...params} label='Organization' fullWidth />}
			/>
		</Stack>
	);
};
